import { type Dispatch, type SetStateAction } from 'react';

import { deleteCookie, getCookie, setCookie } from 'cookies-next/client';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { objectToSnake } from 'ts-case-convert';

import { isFetchError } from '../http/helpers';
import { type AxiosCompatibleError } from '../http/types';
import {
  type MagicLinksRequestingPlatform,
  type RequestMagicLinkRequest,
} from '../magicLinks/aliases';

import {
  type AppleCredentials,
  type EbayCredentials,
  type InvalidLoginResponse,
} from './aliases';
import {
  blacklistedLoginPaths,
  EBAY_SSO_ERROR_CODES,
  INTERNAL_SSO_ERROR_CODES,
} from './constants';

import { PERSISTENT_ID_KEY } from '@/constants/cookies';
import { getPlatform } from '@/modules/activityTracker/helpers.ts';
import {
  COOKIE_ACCOUNT_LINKING_TOKEN_GOOGLE,
  COOKIE_ACCOUNT_LINKING_TOKEN_APPLE,
  COOKIE_ACCOUNT_LINKING_TOKEN_EBAY,
  COOKIE_ACCOUNT_LINKING_SSO_EMAIL,
  GENERIC_ERROR,
  COOKIE_ACCOUNT_LINKING_TOKEN_OPTIONS,
} from '@/modules/auth/constants';
import { CHECKOUT_LANDING_PATH } from '@/modules/checkout/constants';
import { getCheckoutProducts } from '@/modules/checkout/helpers';
import { type CheckoutProduct } from '@/modules/checkout/types';
import {
  type EbayLoginErrorResponse,
  type MfaRequiredErrorResponse,
} from '@/modules/login/aliases';
import {
  type AccountLinkingCredential,
  type ErrorMessage,
  type GoogleSignupCredential,
  isAppleCredentials,
  isEbayCredentials,
} from '@/modules/login/types.ts';
import { getSafeRedirectPath } from '@/modules/routing/getSafeRedirectPath';
import { type User } from '@/modules/user/aliases';
import { updateUserSettings } from '@/modules/user/api';
import { isEmail } from '@/utils/isEmail.ts';

/**
 * The purpose of this helper is to make sure that we are not trying to redirect
 * to a blacklisted redirect path after login. For example, if we started on the
 * forgotten password page and clicked the login button in the nav, the initial redirect
 * arg would be the path for the forgotten password page. However, if we were to login to
 * forgotten password once logged in, we would hit an error so this helper is intended to
 * stop that from happening.
 */
export function getLoginRedirectParams(redirectPath: string) {
  const matches = blacklistedLoginPaths.filter((blacklistedLoginPath) =>
    redirectPath.match(blacklistedLoginPath)
  );
  return matches.length > 0
    ? ''
    : `?redirect=${encodeURIComponent(redirectPath)}`;
}

/**
 * Get parameters for a magic link request based on the provided app cookie.
 *
 * This function determines the platform and whether the app cookie represents
 * an email or username, then constructs the appropriate request object.
 *
 * @param {string} appCookie - The app cookie which is either an email or a username.
 * @returns {Promise<RequestMagicLinkRequest>} A promise that resolves to the magic link request parameters.
 */
export async function getMagicLinkParams(
  appCookie: string,
  platform?: MagicLinksRequestingPlatform
): Promise<RequestMagicLinkRequest> {
  const requestingPlatform =
    platform ||
    ((await getPlatform())?.toLowerCase() as MagicLinksRequestingPlatform);
  const persistentId = getCookie(PERSISTENT_ID_KEY);
  const baseParams = {
    requesting_platform: requestingPlatform,
    persistent_id: persistentId,
  };

  return isEmail(appCookie)
    ? {
        ...baseParams,
        email: appCookie,
      }
    : {
        ...baseParams,
        username: appCookie,
      };
}

export function validateCheckoutRedirect(userId: number, redirect?: string) {
  if (CHECKOUT_LANDING_PATH && redirect?.startsWith(CHECKOUT_LANDING_PATH)) {
    const products = getCheckoutProducts();

    if (!products || !Array.isArray(products)) {
      return null;
    }
    const firstProduct = products[0] as CheckoutProduct;
    if (
      firstProduct.seller?.id &&
      userId &&
      firstProduct.seller?.id !== userId
    ) {
      return redirect;
    }
    if (firstProduct.slug) {
      return `/products/${firstProduct.slug}`;
    }
    return null;
  }
  return redirect;
}

export function validateRedirect(
  initialPath: string | null,
  user: Pick<User, 'id' | 'username'>
) {
  if (!initialPath) {
    return `${window.location.origin}/`;
  }
  let redirect = validateCheckoutRedirect(user.id, initialPath) || '/';
  /* @johnf 21-02-23: when a user is redirected to a url which follows their
   * username, we pass a placeholder [username] and replace it with their
   * actual username here, e.g. when trying to access likes when logged out */
  redirect = redirect?.replace('/[username]/', `/${user.username}/`);

  // Prevent redirecting to external URLs
  const url = new URL(
    getSafeRedirectPath(redirect, window.location.origin) ?? '/',
    window.location.origin
  );

  return url.toString();
}

export async function handleAccountLinking(
  accountLinkingCredential: AccountLinkingCredential | null,
  sendLinkAccountSuccessEvent: (
    accountLinkingCredential: AccountLinkingCredential
  ) => void
) {
  if (accountLinkingCredential) {
    try {
      await updateUserSettings(objectToSnake(accountLinkingCredential));
      sendLinkAccountSuccessEvent(accountLinkingCredential);
    } catch {
      /* If there is an account linking error we can still log them in
                 and repeat the process next time they log in.
              */
      return;
    }
  }
}

export function deleteAccountLinkingCookies() {
  deleteCookie(COOKIE_ACCOUNT_LINKING_TOKEN_GOOGLE);
  deleteCookie(COOKIE_ACCOUNT_LINKING_TOKEN_APPLE);
  deleteCookie(COOKIE_ACCOUNT_LINKING_TOKEN_EBAY);
  deleteCookie(COOKIE_ACCOUNT_LINKING_SSO_EMAIL);
}

type Args = {
  error: AxiosCompatibleError<
    | MfaRequiredErrorResponse
    | ErrorMessage
    | InvalidLoginResponse
    | EbayLoginErrorResponse
  >;
  setError?: Dispatch<SetStateAction<string>>;
  onUserNotFound: () => void;
  onAccountNotLinked: () => void;
};

export function handleSSOError({
  error,
  setError,
  onUserNotFound,
  onAccountNotLinked,
}: Args) {
  if (error.response?.data) {
    if ('code' in error.response.data) {
      const { code } = error.response.data;
      if (code === INTERNAL_SSO_ERROR_CODES.userNotFound) {
        return onUserNotFound();
      }
      if (code === INTERNAL_SSO_ERROR_CODES.accountNotLinked) {
        return onAccountNotLinked();
      }
    }

    if ('error_message' in error.response.data) {
      const { error_message } = error.response.data;
      return setError?.(error_message);
    }
  }

  return setError?.(GENERIC_ERROR);
}

export function getEbaySSOErrorKey(error: unknown) {
  const data = isFetchError(error) ? error.response?.data : undefined;
  const code = data instanceof Object && 'code' in data ? data.code : undefined;

  switch (code) {
    case EBAY_SSO_ERROR_CODES.suspended:
      return 'EbaySSO.Error.Suspended';
    case EBAY_SSO_ERROR_CODES.verificationFailed:
      return 'EbaySSO.Error.VerificationFailed';
    case EBAY_SSO_ERROR_CODES.businessAccount:
      return 'EbaySSO.Error.BusinessAccount';
    default:
      return 'EbaySSO.Error.Generic';
  }
}

export function setAccountLinkingCookies(
  credentials: GoogleSignupCredential | AppleCredentials | EbayCredentials,
  email: string
) {
  deleteAccountLinkingCookies();
  setCookie(
    COOKIE_ACCOUNT_LINKING_SSO_EMAIL,
    email,
    COOKIE_ACCOUNT_LINKING_TOKEN_OPTIONS
  );

  if (isEbayCredentials(credentials)) {
    const { ebay_user_id: ebay_id, ebay_token_reference } = credentials;
    return setCookie(
      COOKIE_ACCOUNT_LINKING_TOKEN_EBAY,
      JSON.stringify({
        ebay_id,
        ebay_token_reference,
      }),
      COOKIE_ACCOUNT_LINKING_TOKEN_OPTIONS
    );
  }

  if (isAppleCredentials(credentials)) {
    const { apple_user_id: appleId, apple_access_token } = credentials;
    return setCookie(
      COOKIE_ACCOUNT_LINKING_TOKEN_APPLE,
      JSON.stringify({
        appleId,
        apple_access_token,
      }),
      COOKIE_ACCOUNT_LINKING_TOKEN_OPTIONS
    );
  }

  return setCookie(
    COOKIE_ACCOUNT_LINKING_TOKEN_GOOGLE,
    credentials,
    COOKIE_ACCOUNT_LINKING_TOKEN_OPTIONS
  );
}
